import values from "lodash-es/values"
import type { FieldErrors, UseFormRegister } from "react-hook-form"

import type { TTFunction, TTranslationKeys } from "$/i18n/i18n"

import type { TInputProps } from "@/3514/components"
import { EPath } from "@/3514/constants"

import { EParticipantOnboardingStep as EStep } from "./participant.onboarding.api.config"
import { type TParticipantOnboardingProfileFormValues as TProfileFormValues } from "./participant.onboarding.ui.types"

const LEGACY_ONBOARDING_STEPS: EStep[] = [EStep._LegacyModules, EStep._LegacyDiagnosticResponses]

const stepToPathnameMap = {
    [EStep.Profile]: EPath.Participant_OnboardingProfile(),
    [EStep.Video]: EPath.Participant_OnboardingVideo(),
    [EStep._LegacyModules]: EPath.Participant_OnboardingModules_Legacy(),
    [EStep.Diagnostic]: EPath.Participant_OnboardingDiagnostic(),
    [EStep._LegacyDiagnosticResponses]: EPath.Participant_OnboardingDiagnosticResults_Legacy(),
    [EStep.Reflection]: EPath.Participant_OnboardingReflection(),
    [EStep.Holds]: EPath.Participant_OnboardingHolds(),
    [EStep.Coach]: EPath.Participant_OnboardingCoach()
}

const routeTitlesMap: {
    [S in Exclude<EStep, EStep.Final>]: ({
        isCoachStepLikertPhase
    }: {
        isCoachStepLikertPhase: boolean
    }) => keyof TTranslationKeys
} = {
    [EStep.Profile]: (): keyof TTranslationKeys => "participantSide.onboarding.profile.title",
    [EStep.Video]: (): keyof TTranslationKeys => "participantSide.onboarding.video.title",
    [EStep._LegacyModules]: null,
    [EStep.Diagnostic]: (): keyof TTranslationKeys => "participantSide.onboarding.diagnostic.title",
    [EStep._LegacyDiagnosticResponses]: null,
    [EStep.Reflection]: (): keyof TTranslationKeys => "participantSide.onboarding.reflection.title",
    [EStep.Holds]: (): keyof TTranslationKeys => "participantSide.onboarding.holds.title",
    [EStep.Coach]: ({ isCoachStepLikertPhase }: { isCoachStepLikertPhase: boolean }): keyof TTranslationKeys =>
        isCoachStepLikertPhase
            ? "participantSide.onboarding.coach.title.questionPhase"
            : "participantSide.onboarding.coach.title.coachPhase.presubmitted"
}

const routeTitleHintsMap: { [S in Exclude<EStep, EStep.Final>]: keyof TTranslationKeys } = {
    [EStep.Profile]: null,
    [EStep.Video]: "participantSide.onboarding.video.titleHint",
    [EStep._LegacyModules]: null,
    [EStep.Diagnostic]: null,
    [EStep._LegacyDiagnosticResponses]: null,
    [EStep.Reflection]: null,
    [EStep.Holds]: "participantSide.onboarding.holds.titleHint",
    [EStep.Coach]: "participantSide.onboarding.coach.subTitle.coachPhase"
}

function renderProfileInputsProps(params: {
    register: UseFormRegister<TProfileFormValues>
    errors: FieldErrors<TProfileFormValues>
    t: TTFunction
    isSubmitting: boolean
}): TInputProps[] {
    const _firstNameErrors: string[] = values(params.errors?.firstName?.types) as []
    const _lastNameErrors: string[] = values(params.errors?.lastName?.types) as []

    return [
        {
            type: "text",
            isRequired: true,
            placeholder: params.t("participantSide.onboarding.profile.form.input.firstName.placeholder"),
            isDisabled: params.isSubmitting,
            isError: !!_firstNameErrors.length,
            errorMessages: _firstNameErrors,
            withSpelling: false,
            mode: "text",
            maxSymbols: 50,
            ...params.register("firstName")
        },
        {
            type: "text",
            isRequired: true,
            placeholder: params.t("participantSide.onboarding.profile.form.input.lastName.placeholder"),
            isDisabled: params.isSubmitting,
            isError: !!_lastNameErrors.length,
            errorMessages: _lastNameErrors,
            withSpelling: false,
            mode: "text",
            maxSymbols: 50,
            ...params.register("lastName")
        }
    ]
}

export default {
    LEGACY_ONBOARDING_STEPS,
    stepToPathnameMap,
    routeTitlesMap,
    routeTitleHintsMap,
    renderProfileInputsProps
}
